import { ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { Location, NgIf } from '@angular/common';

import { Meta, Title } from '@angular/platform-browser';
import {
  IJobsAndCareers,
  IJobsAndCareersCard,
  IJobsAndCareersTab,
  IStrapiDocument,
} from 'src/app/interfaces/strapi';
import { ActivatedRoute } from '@angular/router';
import StrapiService from 'src/app/services/strapi.service';
import { PageComponent } from 'src/app/shared/components/page/page.component';
import { TabData, GenericTabsComponent } from '../generic-tabs/generic-tabs.component';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { RenderMarkdownComponent } from '../../../shared/components/render-markdown/render-markdown.component';
import { JobsAndCareersCardComponent } from './jobs-and-careers-card/jobs-and-careers-card.component';
import { StudentLifeBannerComponent } from '../../../shared/components/student-life-banner/student-life-banner.component';
import { ProfileComponent } from '../profile/profile.component';

enum JobsAndCareersEnum {
  TOPFULL = 'top_full_width',
  BOTTOMFULL = 'bottom_full_width',
}

@Component({
    selector: 'app-jobs-and-careers',
    templateUrl: './jobs-and-careers.component.html',
    styleUrls: ['./jobs-and-careers.component.scss'],
    imports: [
        NgIf,
        BreadcrumbsComponent,
        GenericTabsComponent,
        RenderMarkdownComponent,
        JobsAndCareersCardComponent,
        StudentLifeBannerComponent,
        ProfileComponent,
    ],
})
export class JobsAndCareersComponent extends PageComponent implements OnInit {
  public override pageData: IJobsAndCareers = <IJobsAndCareers>{};

  public currentPage: IJobsAndCareersTab = <IJobsAndCareersTab>{};

  public JobsAndCareersEnum = JobsAndCareersEnum;

  public splitHeaderLayout: boolean = false;

  public pageTabs: TabData[] = [];

  public isLoading: boolean = false;

  public isMobile: boolean = false;

  public paramNavigation: string = '';

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,
    public override readonly strapi: StrapiService,
    public readonly activeRoute: ActivatedRoute,
    public location: Location,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(titleService, metaService, strapi);
  }

  @HostListener('window:resize')
  onScreenChange() {
    this.isMobile = window.innerWidth < 992;
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.strapi.getJobsAndCareers().subscribe({
      next: (data: IStrapiDocument<IJobsAndCareers>) => {
        this.parsePageData(data.data.attributes);
        this.checkForParams();
        this.processPageTabs();
      },
      error: (_error: any) => {
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
        this.cdr.markForCheck()
      },
    });
  }

  private checkForParams(): void {
    this.activeRoute.queryParamMap.subscribe((params) => {
      const tab = params.get('tab');
      if (tab) {
        this.onTabChanged(tab);
        this.paramNavigation = tab;
      }
    });
  }

  protected override parsePageData(page: IJobsAndCareers): void {
    this.pageData = page;
    this.currentPage = this.pageData.tabs[0];
    this.processPageCards();
    this.splitHeaderLayout =
      !!this.pageData.tabs[0].header_image_web?.data?.attributes?.url;
    this.location.replaceState(
      `app/jobs-and-careers?tab=${this.currentPage.tab_id}`
    );
    this.setMetas();
    super.parsePageData(page);
  }

  /**
   * Process tab data
   */
  private processPageTabs(): void {
    this.pageData.tabs.forEach((tab: IJobsAndCareersTab, index: number) => {
      const tabData = { id: tab.tab_id, title: tab.tab_title };
      if (this.paramNavigation === tab.tab_id) {
        this.pageTabs.push({ ...tabData, isActive: true });
      } else {
        this.pageTabs.push({
          ...tabData,
          isActive: this.paramNavigation ? false : index === 0,
        });
      }
    });
  }

  /**
   * Sort the cars array to place the top_full_width cards first and the
   * bottom_full_width cards last.
   */
  private processPageCards() {
    this.currentPage.cards.sort(
      (first: IJobsAndCareersCard, next: IJobsAndCareersCard) => {
        if (first.layout === JobsAndCareersEnum.TOPFULL) {
          return -1;
        }
        if (next.layout === JobsAndCareersEnum.TOPFULL) {
          return 1;
        }
        if (first.layout === JobsAndCareersEnum.BOTTOMFULL) {
          return 1;
        }
        if (next.layout === JobsAndCareersEnum.BOTTOMFULL) {
          return -1;
        }
        return 0;
      }
    );
  }

  /**
   * @param tabUid - tab to navigate to
   * @returns void
   * On a tab change, get the current page
   */
  public onTabChanged(tabUid: string): void {
    this.currentPage =
      this.pageData.tabs.find(
        (tab: IJobsAndCareersTab) => tabUid === tab.tab_id
      ) ?? <IJobsAndCareersTab>{};

    this.splitHeaderLayout =
      !!this.currentPage.header_image_web?.data?.attributes?.url;

    this.location.replaceState(`app/jobs-and-careers?tab=${tabUid}`);
  }

  public navigate(url: string): void {
    window.location.href = url;
  }
}
